import { useEffect } from 'react'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import styled from 'styled-components'

import type _Infra from 'mobx/Infra'
import { injectStores } from 'mobx/injection'
import type { SavedAddress } from 'mobx/Localization/types'
import { AddressLabel } from 'mobx/Localization/types'
import { EVENTS } from 'mobx/Localization/constants'

import { getTranslatedTextByKey } from 'utils/language'

import { respondAbove } from 'styles/mixins'
import TypographyPro from 'themes/TypographyPro'
import {
	StyledPlusIcon,
	StyledSavedAddressButton,
	StyledSavedAddressButtonIcon,
	StyledSavedAddressesContainer,
	StyledSavedAddressesList,
	StyledSavedAddressesPlaceholderIcon,
	StyledSavedAddressesPlaceholderText,
	StyledSavedAddressSubtitle,
	StyledSavedAddressTextsContainer,
	StyledSavedAddressTitle,
} from 'components/localization/steps/AddressSelection/SavedAddresses.styles'
import { EditIcon, TrashCanIconContainer, TrashCanIcon } from 'components/localization/style'
import ButtonBase from 'components/common/ButtonBase'

const labelIconMap: Record<AddressLabel, string> = {
	[AddressLabel.Home]: '/icons/homeLabel.svg',
	[AddressLabel.Work]: '/icons/workLabel.svg',
	[AddressLabel.Other]: '/icons/otherLabel.svg',
}

const getAddressTitle = (address: SavedAddress) => {
	if (address.label === AddressLabel.Work) {
		return getTranslatedTextByKey('webviewFlow.localization.workLabel', 'Work')
	}

	if (address.label === AddressLabel.Home) {
		return getTranslatedTextByKey('webviewFlow.localization.homeLabel', 'Home')
	}

	return address.customLabel || getTranslatedTextByKey('webviewFlow.localization.otherLabel', 'Other')
}

const Container = styled.div`
	max-width: 100%;
	flex: 1;
`

const Title = styled(TypographyPro)`
	text-transform: uppercase;
	color: var(--strokeMenuTitle);
	padding-left: 1.25rem;
	padding-bottom: 1.25rem;

	${respondAbove.md`
		padding-left: 1.75rem;
  	`}
`

const ButtonsContainer = styled.div`
	display: flex;
	gap: 20px;
	margin-top: 28px;
	margin-bottom: 28px;
	flex-direction: column;
	width: 100%;
`

const StyledSavedAddressesEditingContainer = styled(StyledSavedAddressesContainer)`
	width: 100%;
`
const StyledEditingSavedAddressTextsContainer = styled(StyledSavedAddressTextsContainer)`
	flex: 1;
`

const SavedAddresses = ({ Infra }: { Infra: typeof _Infra }) => {
	const { localizationStore } = useStores()
	const { savedAddresses, reachedMaximumSavedAddresses } = localizationStore

	useEffect(() => {
		localizationStore.sendLocalizationEvent(EVENTS.SAVED_ADDRESSES)
	}, [])

	const onAddressClick = async (address: SavedAddress) => {
		Infra.setLoading(true)
		const { deliveryUnavailable } = await localizationStore.startFromSavedAddress(address)
		Infra.setLoading(false)

		if (deliveryUnavailable) {
			informDeliveryNotAvailable()
		}
	}

	const onEditSavedAddressClick = async (address: SavedAddress) => {
		const { deliveryUnavailable } = await localizationStore.startFromSavedAddress(address, true)
		if (deliveryUnavailable) {
			informDeliveryNotAvailable()
			return
		}

		localizationStore.sendLocalizationEvent(EVENTS.EDIT_ADDRESS, {
			action: 'clicked',
			Label: 'edit button',
		})
	}

	const onSaveNewAddressClick = async () => {
		const { deliveryUnavailable } = await localizationStore.startFromSavedAddress()
		if (deliveryUnavailable) {
			informDeliveryNotAvailable()
			return
		}

		localizationStore.sendLocalizationEvent(EVENTS.ADD_NEW_ADDRESS)
	}

	const onDeleteSavedAddress = (addressId: string) => {
		Infra.setNotification({
			open: true,
			title: getTranslatedTextByKey('webviewFlow.localization.addressDeleteConfirmationTitle', 'Delete address?'),
			message: getTranslatedTextByKey(
				'webviewFlow.localization.addressDeleteConfirmationMessage',
				'Are you sure you want to delete this address?\nThis action cannot be undone'
			),
			okText: getTranslatedTextByKey('webviewFlow.localization.addressDeleteConfirmationButton', 'delete address'),
			okAction: () => {
				Infra.closeNotification()
				localizationStore.removeSavedAddress(addressId)
				localizationStore.sendLocalizationEvent(EVENTS.DELETE_ADDRESS, {
					action: 'clicked',
					Label: 'delete button',
				})
			},
			closable: true,
		})
	}

	const informDeliveryNotAvailable = () => {
		Infra.setNotification({
			open: true,
			title: getTranslatedTextByKey('webviewFlow.localization.deliveryNotAvailableTitle', 'Delivery not available'),
			message: getTranslatedTextByKey(
				'webviewFlow.localization.deliveryNotAvailableMessage',
				'The delivery order is currently not available. Please try again during the opening hours'
			),
			okAction: () => {
				Infra.closeNotification()
			},
			closable: true,
		})
	}

	return (
		<Container>
			<Title variant="h2">{getTranslatedTextByKey('webviewFlow.localization.savedAddresses', 'Saved addresses')}</Title>
			<StyledSavedAddressesEditingContainer>
				{savedAddresses.length === 0 && (
					<>
						<StyledSavedAddressesPlaceholderIcon asset="/icons/mapPinGrey.svg" />
						<StyledSavedAddressesPlaceholderText variant="BodyRegular">
							{getTranslatedTextByKey(
								'webviewFlow.localization.noSavedAddresses',
								'There aren’t any saved addresses in your account yet!'
							)}
						</StyledSavedAddressesPlaceholderText>
					</>
				)}

				<StyledSavedAddressesList>
					{savedAddresses.map((address) => (
						<StyledSavedAddressButton
							onClick={() => onAddressClick(address)}
							selected={localizationStore.actualFlow?.savedAddressId === address._id}
							key={address._id}
						>
							<StyledSavedAddressButtonIcon asset={labelIconMap[address.label]} />
							<StyledEditingSavedAddressTextsContainer>
								<StyledSavedAddressTitle variant="SubheadHighlighted">{getAddressTitle(address)}</StyledSavedAddressTitle>
								<StyledSavedAddressSubtitle variant="BodyRegular">{address.formatted}</StyledSavedAddressSubtitle>
							</StyledEditingSavedAddressTextsContainer>
							<EditIcon
								onClick={(e: InputEvent) => {
									e.stopPropagation()
									onEditSavedAddressClick(address)
								}}
							/>
							<TrashCanIconContainer
								onClick={(e: any) => {
									e.stopPropagation()
									onDeleteSavedAddress(address._id)
								}}
							>
								<TrashCanIcon />
							</TrashCanIconContainer>
						</StyledSavedAddressButton>
					))}
				</StyledSavedAddressesList>

				<ButtonsContainer>
					<ButtonBase $type="secondary" onClick={onSaveNewAddressClick} disabled={reachedMaximumSavedAddresses}>
						<StyledPlusIcon />
						{getTranslatedTextByKey('webviewFlow.localization.addNewAddress', 'Add new address')}
					</ButtonBase>
				</ButtonsContainer>
			</StyledSavedAddressesEditingContainer>
		</Container>
	)
}

export default injectStores('Infra')(observer(SavedAddresses))
